import { useEffect } from "react";
import "./sectionFlow.css";

/*
 * Section flow: in scene mode the page moves from one section to the next
 * like slides, not by scrolling through the stretch in between.
 *
 * Inside a section nothing changes: the wheel scrubs its scenes as before.
 * Push past the end of a section (or past the start, going back) and the page
 * jumps to the next one and plays that boundary's transition. Menu links, the
 * hero's buttons, #hash links and the Play button go through the same path.
 *
 * How a transition is drawn: the stage on screen is copied into a fixed layer
 * on top, the page jumps to the next scene underneath, and the copy and the
 * live stage animate against each other. Only transform, opacity and
 * clip-path are animated, so nothing re-lays out while it runs.
 */

export type FlowMode = "push" | "zoom" | "wipe" | "fold";

/** How each boundary is crossed, in page order: home→about, about→experience, experience→projects, projects→contact. */
const BOUNDARY: FlowMode[] = ["push", "zoom", "wipe", "fold"];
const DURATION: Record<FlowMode, number> = { push: 650, zoom: 700, wipe: 700, fold: 800 };
const EASE = "cubic-bezier(.7,0,.2,1)";
/** How far (px) the reader has to keep pushing at a section's edge before it moves on. */
const PUSH = 48;
/** After a transition, swallow the wheel's leftover momentum this long (ms). */
const COOLDOWN = 380;

interface Track {
    el: HTMLElement;
    stage: HTMLElement;
    /** Scroll positions where the scene's progress is 0 and 1. */
    start: number;
    end: number;
}

interface Sec {
    id: string;
    tracks: Track[];
    start: number;
    end: number;
}

let secs: Sec[] = [];
type FlowListener = (mode: FlowMode, dir: number, to: string) => void;
const listeners = new Set<FlowListener>();

/** Hear about every section transition as it starts (the docked badge reacts to them). */
export function onFlow(fn: FlowListener) {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}
let active = false;
let busy = false;
/** The transition in progress, so a second caller can wait for it. */
let running: Promise<void> = Promise.resolve();
let cooldownUntil = 0;
/** Our own jumps fire scroll events too; the guard ignores them until then. */
let ignoreUntil = 0;
const push = { acc: 0, dir: 0, at: 0 };

function measure() {
    const byId = new Map<string, Sec>();
    secs = [];
    document.querySelectorAll<HTMLElement>("main .scene-track").forEach((el) => {
        const stage = el.querySelector<HTMLElement>(":scope > .scene-stage");
        const id = el.closest("section")?.id;
        if (!stage || !id) return;
        // Same span SceneTrack uses for its progress.
        const start = el.getBoundingClientRect().top + window.scrollY;
        const end = start + Math.max(1, el.offsetHeight - stage.offsetHeight);
        let s = byId.get(id);
        if (!s) {
            s = { id, tracks: [], start, end };
            byId.set(id, s);
            secs.push(s);
        }
        s.tracks.push({ el, stage, start, end });
        s.end = end;
    });
}

/** The section at scroll position `y`; `gap` when `y` is in the stretch after section `k`. */
function locate(y: number) {
    for (let k = 0; k < secs.length; k++) {
        const s = secs[k];
        if (y < s.start - 1) return { k: Math.max(0, k - 1), gap: k > 0 };
        if (y <= s.end + 1) return { k, gap: false };
    }
    return { k: secs.length - 1, gap: false };
}

/** The track whose stage is on screen at `y` (or the nearest one). */
function trackAt(y: number) {
    let best: Track | null = null;
    let dist = Infinity;
    secs.forEach((s) =>
        s.tracks.forEach((t) => {
            const d = y < t.start ? t.start - y : y > t.end ? y - t.end : 0;
            if (d < dist) {
                dist = d;
                best = t;
            }
        })
    );
    return best as Track | null;
}

function jump(y: number) {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    ignoreUntil = performance.now() + 80;
    window.scrollTo(0, y);
    html.style.scrollBehavior = prev;
}

/**
 * A copy of the stage on screen, inside a fixed layer. The copy is wrapped in
 * stand-ins for its ancestors (same tags and classes, no ids), so styles
 * scoped by an ancestor — `.scene-index .hero`, a section's tint — still match.
 */
function snapshot(track: Track) {
    const overlay = document.createElement("div");
    overlay.className = "flow-overlay";
    overlay.setAttribute("aria-hidden", "true");
    overlay.inert = true;

    const shade = document.createElement("div");
    shade.className = "flow-shade";
    overlay.appendChild(shade);

    const chain: HTMLElement[] = [];
    for (let n: HTMLElement | null = track.el; n && n.tagName !== "MAIN"; n = n.parentElement) chain.unshift(n);
    let host: HTMLElement = overlay;
    chain.forEach((n) => {
        const c = document.createElement(n.tagName.toLowerCase());
        c.className = n.className;
        c.setAttribute("style", `${n.getAttribute("style") ?? ""};position:absolute;inset:0;width:100%;height:100%;min-height:0;margin:0;padding:0`);
        host.appendChild(c);
        host = c;
    });

    const clone = track.stage.cloneNode(true) as HTMLElement;
    clone.style.position = "absolute";
    clone.style.inset = "0";
    clone.style.width = "100%";
    clone.style.height = "100%";
    host.appendChild(clone);

    const bar = document.createElement("div");
    bar.className = "flow-bar";
    overlay.appendChild(bar);
    return { overlay, clone, shade, bar };
}

function play(mode: FlowMode, dir: number, parts: ReturnType<typeof snapshot>, incoming: HTMLElement) {
    const { clone, shade, bar } = parts;
    const o: KeyframeAnimationOptions = { duration: DURATION[mode], easing: EASE, fill: "both" };
    const bg = getComputedStyle(document.documentElement).getPropertyValue("--bg-rgb").trim() || "20, 24, 31";
    // The copy needs a solid ground when it covers the live stage; it fades
    // in fast so the background field doesn't visibly switch off.
    const ground = [
        { backgroundColor: `rgba(${bg}, 0)`, offset: 0 },
        { backgroundColor: `rgba(${bg}, 1)`, offset: 0.12 },
        { backgroundColor: `rgba(${bg}, 1)`, offset: 1 },
    ];
    const hideFirst = [
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: 0.1 },
        { opacity: 1, offset: 0.14 },
        { opacity: 1, offset: 1 },
    ];

    switch (mode) {
        case "push":
            return [
                clone.animate([{ transform: "translateY(0)" }, { transform: `translateY(${-100 * dir}%)` }], o),
                incoming.animate([{ transform: `translateY(${100 * dir}%)` }, { transform: "translateY(0)" }], o),
            ];
        case "zoom":
            return [
                clone.animate(
                    [
                        { transform: "scale(1)", opacity: 1 },
                        { transform: `scale(${dir > 0 ? 1.28 : 0.78})`, opacity: 0 },
                    ],
                    o
                ),
                incoming.animate(
                    [
                        { transform: `scale(${dir > 0 ? 0.8 : 1.25})`, opacity: 0 },
                        { transform: "scale(1)", opacity: 1 },
                    ],
                    o
                ),
            ];
        case "wipe": {
            const tint = getComputedStyle(incoming).getPropertyValue("--scene-tint").trim() || "255, 255, 255";
            bar.style.background = `rgb(${tint})`;
            bar.style.boxShadow = `0 0 24px 4px rgba(${tint}, 0.65)`;
            return [
                clone.animate(
                    [{ clipPath: "inset(0 0 0 0)" }, { clipPath: dir > 0 ? "inset(0 0 0 100%)" : "inset(0 100% 0 0)" }],
                    o
                ),
                clone.animate(ground, { ...o, easing: "linear" }),
                incoming.animate(hideFirst, { ...o, easing: "linear" }),
                bar.animate(
                    [
                        { left: dir > 0 ? "0%" : "100%", opacity: 1 },
                        { left: dir > 0 ? "100%" : "0%", opacity: 1 },
                    ],
                    o
                ),
            ];
        }
        case "fold":
            clone.style.transformOrigin = dir > 0 ? "50% 0" : "50% 100%";
            return [
                clone.animate(
                    [
                        { transform: "perspective(1500px) rotateX(0deg)", opacity: 1 },
                        { transform: `perspective(1500px) rotateX(${dir > 0 ? -100 : 100}deg)`, opacity: 0.3 },
                    ],
                    o
                ),
                clone.animate(ground, { ...o, easing: "linear" }),
                shade.animate([{ opacity: 0.55 }, { opacity: 0 }], o),
                incoming.animate(hideFirst, { ...o, easing: "linear" }),
                incoming.animate([{ transform: "scale(.96)" }, { transform: "scale(1)" }], o),
            ];
    }
}

/** Phones: freeze native scrolling (and any fling) while a transition runs. */
const coarse = () => window.matchMedia("(pointer: coarse)").matches;
function lock(on: boolean) {
    if (!coarse()) return;
    const v = on ? "hidden" : "";
    document.documentElement.style.overflow = v;
    document.body.style.overflow = v;
}

/** Move from section `from` to `to`, landing at the start or the end of `to`. */
function transition(from: number, to: number, land: "start" | "end") {
    if (busy) return running;
    if (from === to) return Promise.resolve();
    running = run(from, to, land);
    return running;
}

async function run(from: number, to: number, land: "start" | "end") {
    busy = true;
    lock(true);
    try {
        measure();
        const target = secs[to];
        const outTrack = trackAt(window.scrollY);
        if (!target || !outTrack) return;
        const inTrack = land === "start" ? target.tracks[0] : target.tracks[target.tracks.length - 1];
        const dir = to > from ? 1 : -1;
        const mode = BOUNDARY[dir > 0 ? to - 1 : to] ?? "push";

        const parts = snapshot(outTrack);
        // Copy on top and the jump underneath in the same frame: no gap shows.
        document.body.appendChild(parts.overlay);
        jump(land === "start" ? inTrack.start : inTrack.end);

        listeners.forEach((fn) => fn(mode, dir, target.id));
        const anims = play(mode, dir, parts, inTrack.stage);
        await Promise.all(anims.map((a) => a.finished.catch(() => undefined)));
        anims.forEach((a) => a.cancel());
        parts.overlay.remove();
    } finally {
        busy = false;
        lock(false);
        cooldownUntil = performance.now() + COOLDOWN;
        push.acc = 0;
    }
}

/**
 * The reader pushing on the wheel/finger by `dy` px. Returns true when that
 * push meets a section's edge and has been taken over (scroll prevented).
 */
function onPush(dy: number) {
    const y = window.scrollY;
    const { k, gap } = locate(y);
    if (gap) return false;
    const s = secs[k];
    const now = performance.now();
    const goingOn = dy > 0 && k < secs.length - 1 && y + dy > s.end;
    const goingBack = dy < 0 && k > 0 && y + dy < s.start;
    if (!goingOn && !goingBack) {
        push.acc = 0;
        return false;
    }
    // Finish the current scene exactly at its edge, then count the overflow.
    const edge = goingOn ? s.end : s.start;
    const over = Math.abs(y + dy - edge);
    if (Math.abs(y - edge) > 0.5) jump(edge);
    const d = Math.sign(dy);
    if (now - push.at > 250 || d !== push.dir) push.acc = 0;
    push.acc += over;
    push.at = now;
    push.dir = d;
    if (push.acc >= PUSH) void transition(k, goingOn ? k + 1 : k - 1, goingOn ? "start" : "end");
    return true;
}

const SCROLL_KEYS = new Set(["ArrowDown", "ArrowUp", "PageDown", "PageUp", " ", "Home", "End"]);

/**
 * Turns section flow on while `enabled` (scene mode on the one-page layout).
 * The plain layout keeps ordinary scrolling.
 */
export function useSectionFlow(enabled: boolean) {
    useEffect(() => {
        if (!enabled) return;
        active = true;
        measure();
        let lastY = window.scrollY;
        let touchY = 0;

        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || (e.target as Element | null)?.closest?.("textarea")) return;
            if (busy || performance.now() < cooldownUntil) {
                e.preventDefault();
                return;
            }
            const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * window.innerHeight : e.deltaY;
            if (dy && onPush(dy)) e.preventDefault();
        };
        const onTouchStart = (e: TouchEvent) => {
            touchY = e.touches[0]?.clientY ?? 0;
            push.acc = 0;
        };
        const onTouchMove = (e: TouchEvent) => {
            const cy = e.touches[0]?.clientY ?? touchY;
            const dy = touchY - cy;
            touchY = cy;
            if (busy) {
                if (e.cancelable) e.preventDefault();
                return;
            }
            // Once the browser is mid-scroll it won't let the move be cancelled;
            // the scroll guard below catches that case instead.
            if (e.cancelable && dy && onPush(dy)) e.preventDefault();
        };
        // Anything that lands in the stretch between two sections (scrollbar
        // drag, keys, a fling) finishes the move as a transition.
        const onScroll = () => {
            const y = window.scrollY;
            const prev = lastY;
            lastY = y;
            if (busy || performance.now() < ignoreUntil) return;
            const { k, gap } = locate(y);
            if (!gap) return;
            if (y >= prev) void transition(k, k + 1, "start");
            else void transition(k + 1, k, "end");
        };
        const onKey = (e: KeyboardEvent) => {
            if (busy && SCROLL_KEYS.has(e.key)) e.preventDefault();
        };
        // In-page links to a section (the hero's buttons) take the same path.
        const onClick = (e: MouseEvent) => {
            const a = (e.target as Element | null)?.closest?.('a[href^="#"]');
            const id = a?.getAttribute("href")?.slice(1);
            if (!id || !goToSection(id)) return;
            e.preventDefault();
            window.history.replaceState(null, "", `#${id}`);
        };

        const ro = new ResizeObserver(() => measure());
        ro.observe(document.body);
        window.addEventListener("resize", measure);
        window.addEventListener("wheel", onWheel, { passive: false });
        window.addEventListener("touchstart", onTouchStart, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("keydown", onKey);
        document.addEventListener("click", onClick, true);
        return () => {
            active = false;
            ro.disconnect();
            window.removeEventListener("resize", measure);
            window.removeEventListener("wheel", onWheel);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("keydown", onKey);
            document.removeEventListener("click", onClick, true);
        };
    }, [enabled]);
}

/**
 * Go to a section by id. Returns false when section flow is off (or the id
 * isn't a section), so the caller falls back to ordinary scrolling.
 */
export function goToSection(id: string, opts: { instant?: boolean } = {}) {
    if (!active) return false;
    measure();
    const t = secs.findIndex((s) => s.id === id);
    if (t < 0) return false;
    const target = secs[t].tracks[0].start;
    if (opts.instant) {
        jump(target);
        return true;
    }
    if (busy) return true;
    const { k } = locate(window.scrollY);
    if (k === t) window.scrollTo({ top: target, behavior: "smooth" });
    else void transition(k, t, "start");
    return true;
}

/** Whether moving the scroll position from `a` to `b` would cross into another section. */
export function crossesSection(a: number, b: number) {
    if (!active) return false;
    measure();
    return locate(a).k !== locate(b).k;
}

/** Step to the next (`1`) or previous (`-1`) section. Resolves when the transition ends. */
export function stepSection(dir: 1 | -1) {
    if (busy) return running;
    measure();
    const { k } = locate(window.scrollY);
    const to = k + dir;
    if (!active || to < 0 || to >= secs.length) return Promise.resolve();
    return transition(k, to, dir > 0 ? "start" : "end");
}
